import { useEffect, useMemo, useState } from 'react';
import { uid } from 'uid';
import Badge from '../Common/Badge';
import ChipsAutocomplete from '../Common/ChipsAutocomplete';

interface Chip {
  id: string;
  title: string;
}

interface Props {
  title?: string;
  editable?: boolean;
  selected: Chip[];
  setSelected?: (selected: Chip[]) => void;
  filterQuery?: (query: string) => string;
  options: Chip[];
  update: () => void;
}

export default function SettingSectionChipsField({
  title,
  editable,
  selected,
  setSelected,
  filterQuery,
  options,
  update,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [initialValue, setInitialValue] = useState<Chip[]>(selected);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (editing) {
      setInitialValue(selected);
    }
  }, [editing]);

  const allOptions = useMemo(() => {
    const cleanQuery = query.trim();

    const optionsAndSelected = [
      ...selected.filter((s) => !options.some((o) => o.id === s.id)),
      ...options,
    ];

    if (cleanQuery) {
      return [
        ...(optionsAndSelected.every(
          (o) => o.title.toLowerCase() !== cleanQuery.toLowerCase(),
        )
          ? [{ id: uid(8), title: cleanQuery }]
          : []),
        ...optionsAndSelected.filter((o) =>
          o.title.toLowerCase().includes(cleanQuery.toLowerCase()),
        ),
      ];
    }

    return optionsAndSelected;
  }, [options, query, selected]);

  return (
    <div className="pt-6 sm:flex sm:flex-wrap sm:items-center">
      <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">
        {title}
      </dt>
      <dd className="mt-1 flex flex-wrap items-start justify-between gap-2 gap-x-6 sm:mt-0 sm:flex-auto">
        {!editing ? (
          <div className="flex flex-wrap gap-2">
            {selected?.length ? (
              selected.map((e) => (
                <Badge key={e.id} title={e.title} rounded={false} hideDot />
              ))
            ) : (
              <div className="mt-1 text-sm text-gray-500">None Selected</div>
            )}
          </div>
        ) : (
          <ChipsAutocomplete<Chip>
            getValue={(c) => c?.title}
            selected={selected}
            onChange={(s) => {
              setSelected?.(s);
            }}
            onInputChange={(t) => setQuery(t)}
            getOptionId={(t) => t.id}
            options={allOptions}
            filterQuery={filterQuery}
          />
        )}
        <div className="flex gap-4">
          {editing ? (
            <button
              type="button"
              className="font-semibold text-gray-600 hover:text-gray-500"
              onClick={() => {
                setSelected?.(initialValue);
                setEditing((a) => !a);
              }}
            >
              Cancel
            </button>
          ) : (
            <></>
          )}
          {editable ? (
            <button
              type="button"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
              onClick={() => {
                setEditing((a) => !a);
                if (editing) {
                  update();
                }
              }}
            >
              {editing ? 'Save' : 'Edit'}
            </button>
          ) : (
            <></>
          )}
        </div>
      </dd>
    </div>
  );
}
